// Zebra Browser Print (issue #305): Zebra's own small program on a packing
// station PC that lets this page send a label straight to a Zebra printer
// plugged into (or networked to) that PC, in the printer's own language
// (ZPL), with no print dialog. It listens on this PC only, so nothing here
// leaves the PC.
//
// The calls are Browser Print's own: /available lists the printers it can
// see, /write sends data to one, /read reads back what the printer said.
// Bodies go as text/plain, the same as Zebra's own BrowserPrint.js, so the
// browser sends them without a CORS preflight.

export const BROWSER_PRINT_DOWNLOAD_URL = 'https://www.zebra.com/us/en/support-downloads/printer-software/browser-print.html';

const RESOLUTIONS = [152, 203, 300, 600];
const DPI_CACHE_KEY = 'optim.zebraPrinterDpi';

// Always Browser Print's plain http port, even from an https page: the
// browser counts http://localhost as secure, so this isn't blocked as
// mixed content. Its https port (9101) uses a self-signed certificate,
// and once that's been clicked past, Chrome marks every Optim page
// "Not secure" until it restarts.
const BASE_URL = 'http://localhost:9100';

export class BrowserPrintError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}

const notRunning = () => new BrowserPrintError(
  'not_running',
  'Zebra Browser Print isn\'t running on this PC. Start it from the Windows Start menu (it then sits in the system tray), or download and install it from Zebra. If Browser Print asks whether to allow this site, choose Yes; if the browser asks to let this site reach devices on your network, allow it.',
);

const unreachable = (printerName) => new BrowserPrintError(
  'printer_unreachable',
  `${printerName || 'The printer'} can't be reached. Check it's switched on, connected to this PC and has labels loaded, then try again.`,
);

const tooSlow = () => new BrowserPrintError(
  'too_slow',
  "Zebra Browser Print didn't answer in time. It may still be searching the network for printers: try again in a moment.",
);

async function call(path, body, timeoutMs = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, body === undefined
      ? { signal: controller.signal }
      : { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: JSON.stringify(body), signal: controller.signal });
  } catch (err) {
    throw err.name === 'AbortError' ? tooSlow() : notRunning();
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const error = new BrowserPrintError('failed', text || `Browser Print answered ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.text();
}

// The printers Browser Print can see from this PC, each { name, uid,
// connection, deviceType, provider, manufacturer, version } — kept whole,
// since /write and /read take the device back exactly as listed. Its
// network search can take several seconds, so this waits longer.
async function listedPrinters() {
  const text = await call('/available', undefined, 30000);
  let data;
  try {
    data = JSON.parse(text || '{}');
  } catch {
    data = {};
  }
  const list = Array.isArray(data) ? data : (data.printer || []);
  return list.filter((d) => d && d.uid && (!d.deviceType || d.deviceType === 'printer'));
}

// Each printer once. Browser Print's network search can list the same
// printer many times over, and a printer added by hand in Browser Print
// ("myZebra1", uid 192.168.10.31:9100) is also listed as found
// ("ZD421-203dpi (192.168.10.31)", uid 192.168.10.31); the name given by
// hand wins.
export async function availablePrinters() {
  const byAddress = new Map();
  (await listedPrinters()).forEach((p) => {
    const address = String(p.uid).replace(/:9100$/, '');
    const named = !String(p.name || '').includes(`(${address})`);
    const kept = byAddress.get(address);
    if (!kept || (named && !kept.named)) byAddress.set(address, { printer: p, named });
  });
  return [...byAddress.values()].map((v) => v.printer);
}

// The printer as it's listed now, or an error saying it can't be reached.
// Looked for in the full list, so a link to any of a printer's entries
// still works.
async function findPrinter(device) {
  const printers = await listedPrinters();
  const found = printers.find((p) => p.uid === device.uid);
  if (!found) throw unreachable(device.name);
  return found;
}

async function write(device, data) {
  try {
    await call('/write', { device, data });
  } catch (err) {
    if (err.code === 'not_running' || err.code === 'too_slow') throw err;
    throw unreachable(device.name);
  }
}

const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

function readCache() {
  try {
    return JSON.parse(window.localStorage.getItem(DPI_CACHE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeCache(uid, dpi) {
  try {
    window.localStorage.setItem(DPI_CACHE_KEY, JSON.stringify({ ...readCache(), [uid]: dpi }));
  } catch {
    // Nothing to do — it's asked of the printer again next time.
  }
}

// The resolution the printer last told this PC, if it has.
export function knownDpi(device) {
  return device ? readCache()[device.uid] || null : null;
}

// Asks the printer its resolution (203 or 300 dpi on a ZD421). Its answer
// can take a moment to arrive, so /read is tried a few times. If it never
// answers, the last answer this PC had from it is used.
export async function printerDpi(device) {
  await write(device, '! U1 getvar "head.resolution.in_dpi"\r\n');
  for (let i = 0; i < 6; i += 1) {
    await pause(150);
    let text = '';
    try {
      text = await call('/read', { device }, 4000);
    } catch (err) {
      if (err.code === 'not_running') throw err;
    }
    const match = String(text).match(/\d{3}/);
    const dpi = match ? Number(match[0]) : null;
    if (RESOLUTIONS.includes(dpi)) {
      writeCache(device.uid, dpi);
      return dpi;
    }
  }
  const cached = knownDpi(device);
  if (cached) return cached;
  throw new BrowserPrintError('no_resolution', `${device.name} didn't say what resolution it prints at. Check it's switched on and connected, then try again.`);
}

// Sends a label to the printer, checking first that it's there.
export async function sendToPrinter(device, zpl) {
  const printer = await findPrinter(device);
  await write(printer, zpl);
  return printer;
}

// The printer, checked as reachable, and its resolution.
export async function preparePrinter(device) {
  const printer = await findPrinter(device);
  const dpi = await printerDpi(printer);
  return { printer, dpi };
}
